import { api } from './client'
import type { ProductionOperationType } from './productieRute'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Productie/OrdineModels.cs ----------

export type ProductionOrderStatus = 'Planificat' | 'Lansat' | 'InPregatire' | 'InExecutie' | 'PartialFinalizat' | 'Finalizat' | 'Inchis' | 'Suspendat' | 'Anulat'
export type ProductionPriority = 'Scazuta' | 'Normala' | 'Ridicata' | 'Urgenta'

export interface ProductionOrderListItem {
  id: string; number: string; productArticleId: string; productArticleCode: string; productArticleDescription: string
  plannedQuantityThousands: number; plannedStartDate: string; plannedEndDate: string
  equipmentId: string; equipmentName: string; priority: ProductionPriority; status: ProductionOrderStatus; hasRoute: boolean
}

export interface ProductionOrderDetail {
  id: string; number: string; productArticleId: string; productArticleCode: string; productArticleDescription: string
  bomId: string; bomCode: string; routeId: string | null; routeNumber: string | null
  warehouseId: string; warehouseName: string; plannedQuantityThousands: number; plannedStartDate: string; plannedEndDate: string
  equipmentId: string; equipmentName: string; moldId: string | null; moldName: string | null; shiftNumber: number | null
  operatorUserId: string | null; operatorUserName: string | null; sourceOrderId: string | null; sourceOrderNumber: string | null
  priority: ProductionPriority; status: ProductionOrderStatus; launchedAt: string | null
  actualStartAt: string | null; actualEndAt: string | null; notes: string | null; estimatedCompletionAt: string | null
}

export interface SaveProductionOrderPayload {
  productArticleId: string; warehouseId: string; plannedQuantityThousands: number; plannedStartDate: string; plannedEndDate: string
  equipmentId: string; moldId: string | null; shiftNumber: number | null; operatorUserId: string | null; sourceOrderId: string | null
  priority: ProductionPriority; notes: string | null
}

export interface MaterialAllocation {
  id: string; componentArticleId: string; componentArticleCode: string; componentArticleDescription: string
  warehouseId: string; warehouseName: string; zoneId: string | null; zoneName: string | null
  locationId: string | null; locationCode: string | null; lotNumber: string | null
  quantity: number; consumedQuantity: number; isManualOverride: boolean; overrideReason: string | null
}

export interface LaunchOverrideBucket { zoneId: string | null; locationId: string | null; lotNumber: string | null; quantity: number }
export interface LaunchOverride { componentArticleId: string; reason: string; buckets: LaunchOverrideBucket[] }
export interface LaunchPayload { overrides: LaunchOverride[] | null }

export interface ExpiryRiskEntry {
  articleId: string; articleCode: string; articleDescription: string; lotNumber: string; expiryDate: string
  liberQuantity: number; estimatedValueAtRisk: number; plannedOrdersUsingArticle: number
}

export const listProductionOrders = () => api.get<ProductionOrderListItem[]>('/productie/ordine').then((r) => r.data)
export const getProductionOrder = (id: string) => api.get<ProductionOrderDetail>(`/productie/ordine/${id}`).then((r) => r.data)
export const createProductionOrder = (payload: SaveProductionOrderPayload) =>
  api.post<{ id: string }>('/productie/ordine', payload).then((r) => r.data)
export const updateProductionOrder = (id: string, payload: SaveProductionOrderPayload) => api.put(`/productie/ordine/${id}`, payload)
export const launchProductionOrder = (id: string, payload: LaunchPayload) =>
  api.post<{ pendingApproval: boolean }>(`/productie/ordine/${id}/lanseaza`, payload).then((r) => r.data)
export const getMaterialAllocations = (id: string) => api.get<MaterialAllocation[]>(`/productie/ordine/${id}/alocare-materiale`).then((r) => r.data)
export const suspendProductionOrder = (id: string) => api.post(`/productie/ordine/${id}/suspenda`)
export const reactivateProductionOrder = (id: string) => api.post(`/productie/ordine/${id}/reactiveaza`)
export const cancelProductionOrder = (id: string) => api.post(`/productie/ordine/${id}/anuleaza`)

export const getExpiryRisk = (thresholdDays = 30) =>
  api.get<ExpiryRiskEntry[]>('/productie/plan/risc-expirare', { params: { prag: thresholdDays } }).then((r) => r.data)

export interface OverdueMaintenanceEntry {
  scheduleId: string; equipmentId: string; equipmentCode: string; equipmentName: string; interventionType: string
  nextDueDate: string; overdue: boolean; escalated: boolean
}
export const getOverdueMaintenance = () =>
  api.get<OverdueMaintenanceEntry[]>('/productie/plan/mentenanta-restanta').then((r) => r.data)

// ---------- partea IV — execuție ----------

export type DowntimeCause = 'DefectiuneMecanica' | 'LipsaMaterial' | 'SchimbMatrita' | 'Pauza' | 'Curatare' | 'LipsaUtilitate' | 'Altele'

export interface ReportProductionPayload {
  goodQuantityThousands: number; scrapQuantityThousands: number; finishedLotNumber: string | null
  zoneId: string | null; locationId: string | null; notes: string | null
}
export interface ReportProductionResult { scrapRateWarning: boolean }

export interface ProductionOrderReport {
  id: string; reportedAt: string; goodQuantityThousands: number; scrapQuantityThousands: number
  finishedLotNumber: string; zoneName: string | null; locationCode: string | null; notes: string | null; reportedByUserName: string
}
export interface ConsumptionDeviation {
  componentArticleId: string; componentArticleCode: string; componentArticleDescription: string
  normedQuantity: number; actualQuantity: number; deviationPercent: number
}
export interface ProductionOrderReportsResponse { reports: ProductionOrderReport[]; consumptionDeviations: ConsumptionDeviation[] }

export interface RecordDowntimePayload { startedAt: string; cause: DowntimeCause; notes: string | null }
export interface Downtime { id: string; startedAt: string; endedAt: string | null; cause: DowntimeCause; notes: string | null }

export interface Oee { quality: number; availability: number | null; performance: number | null }

export interface TraceabilityMovement { lotNumber: string; productionOrderId: string; productionOrderNumber: string; quantity: number; createdAt: string }
export interface TraceabilityOperationStep {
  sequenceOrder: number; operationName: string; inputLotNumber: string | null; outputLotNumber: string | null
  goodQuantity: number; scrapQuantity: number; status: OperationRunStatus
}
export interface TraceabilityResult {
  lotNumber: string; isFinishedProductLot: boolean; relatedMovements: TraceabilityMovement[]; operationChain: TraceabilityOperationStep[]
}

export const startExecution = (id: string) => api.post(`/productie/ordine/${id}/start-executie`)
export const reportProduction = (id: string, payload: ReportProductionPayload) =>
  api.post<ReportProductionResult>(`/productie/ordine/${id}/raporteaza-productie`, payload).then((r) => r.data)
export const getProductionReports = (id: string) => api.get<ProductionOrderReportsResponse>(`/productie/ordine/${id}/rapoarte`).then((r) => r.data)
export const recordDowntime = (id: string, payload: RecordDowntimePayload) => api.post(`/productie/ordine/${id}/opriri`, payload)
export const endDowntime = (id: string, downtimeId: string) => api.post(`/productie/ordine/${id}/opriri/${downtimeId}/incheie`)
export const getDowntimes = (id: string) => api.get<Downtime[]>(`/productie/ordine/${id}/opriri`).then((r) => r.data)
export const finalizePartial = (id: string) => api.post(`/productie/ordine/${id}/finalizeaza-partial`)
export const closeProductionOrder = (id: string) => api.post(`/productie/ordine/${id}/inchide`)
export const getOee = (id: string) => api.get<Oee>(`/productie/ordine/${id}/oee`).then((r) => r.data)
export const getTraceability = (lotNumber: string) =>
  api.get<TraceabilityResult>(`/productie/ordine/trasabilitate/${encodeURIComponent(lotNumber)}`).then((r) => r.data)

// ---------- Catalog de rapoarte, „Ordine de producție" (§12.2 #10) ----------

export type ProductionOrderReportBucket = 'Deschis' | 'Intarziat' | 'Finalizat'

export interface ProductionOrderStatusReportRow {
  id: string; number: string; productArticleId: string; productArticleCode: string; productArticleDescription: string
  plannedQuantityThousands: number; plannedStartDate: string; plannedEndDate: string
  equipmentId: string; equipmentName: string; status: ProductionOrderStatus; bucket: ProductionOrderReportBucket
}

export interface ProductionOrderStatusReportFilters { from?: string; to?: string }

export const getProductionOrdersStatusReport = (filters?: ProductionOrderStatusReportFilters) =>
  api.get<ProductionOrderStatusReportRow[]>('/productie/ordine/raport', { params: filters }).then((r) => r.data)
export const getProductionOrdersStatusReportExportUrl = (filters?: ProductionOrderStatusReportFilters) =>
  api.get<Blob>('/productie/ordine/raport/export', { params: filters, responseType: 'blob' }).then((r) => r.data)

// ---------- partea VI — execuție multi-pas pe rută (FR-OPE-023/024/027/029) ----------

export type OperationRunStatus = 'Planificat' | 'InExecutie' | 'Finalizat'

export interface OperationRun {
  id: string; productionRouteOperationId: string; sequenceOrder: number; operationName: string; operationType: ProductionOperationType
  allowsOverlapWithNext: boolean; status: OperationRunStatus; cumulativeGoodQuantity: number; cumulativeScrapQuantity: number
  availableForNextOperation: number | null; inputLotNumber: string | null; outputLotNumber: string | null
  actualStartAt: string | null; actualEndAt: string | null; estimatedStartAt: string | null; estimatedEndAt: string | null
}

export interface ReportOperationPayload {
  goodQuantityThousands: number; scrapQuantityThousands: number; lotNumber: string | null
  zoneId: string | null; locationId: string | null; notes: string | null
}
export interface ReportOperationResult { scrapRateWarning: boolean; status: OperationRunStatus }

export const getOperationRuns = (id: string) => api.get<OperationRun[]>(`/productie/ordine/${id}/operatii`).then((r) => r.data)
export const reportOperation = (id: string, runId: string, payload: ReportOperationPayload) =>
  api.post<ReportOperationResult>(`/productie/ordine/${id}/operatii/${runId}/raporteaza`, payload).then((r) => r.data)
